import { format, parseISO } from 'date-fns';
import { type FC } from 'react';

import { Esg } from '@/api/esg/models/Esg';
import { Center } from '@/components/Center';
import { Gauge } from '@/components/Charts';
import { Column, Row } from '@/components/Grid';
import { ConnectPopover } from '@/components/Pretzel/ConnectPopover';
import { Show } from '@/components/Show';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { useUserDateFormat } from '@/i18n/hooks';

import { EsgCarbonFootprint } from './widgets/EsgCarbonFootprint/EsgCarbonFootprint';
import { EsgExclusions } from './widgets/EsgExclusions/EsgExclusions';
import { EsgFundList } from './widgets/EsgFundList/EsgFundList';
import { EsgOverviewValueList } from './widgets/EsgOverview/EsgOverviewValueList';
import { EsgSolutionsChart } from './widgets/EsgSolutions/EsgSolutionsChart';
import { EsgTaxonomyWidget } from './widgets/EsgTaxonomy';

interface Props {
  data: Esg;
}

export const EsgDashboard: FC<Props> = ({ data }) => {
  const texts = useTexts();
  const dateFormat = useUserDateFormat();

  return (
    <WithGap data-test="esg-dashboard">
      <div className="font-details">
        {fmsWithTemplate(texts.common.lastUpdated, {
          date: format(parseISO(data.updatedDate), dateFormat),
        })}
      </div>

      <h2>{texts.pages.esg.title}</h2>

      <WithGap gap="64">
        <Row>
          <Column $span={6}>
            <WithGap data-test="esg-overview-widget">
              <h3>{texts.pages.esg.overview.title}</h3>

              <Center horizontal vertical>
                <Gauge
                  percent={data.solutionCompanies.kpi.value}
                  description={texts.pages.esg.overview.sustainableInvestments}
                  size={300}
                />
              </Center>

              <EsgOverviewValueList
                solutionCompanies={data.solutionCompanies}
                euTaxonomyAlignment={data.euTaxonomyAlignment}
                euTaxonomyEligibility={data.euTaxonomyEligibility}
                carbonIntensities={data.carbonIntensities}
                pai11GhgEmissionsSum={data.pai11GhgEmissionsSum}
              />
            </WithGap>
          </Column>

          <Column $span={6}>
            <EsgFundList portfolioGroupId={data.portfolioGroupId} />
          </Column>
        </Row>

        <Row>
          <Column $span={12}>
            <WithGap>
              <h2>{texts.pages.esg.carbonFootprint.title}</h2>

              <EsgCarbonFootprint
                esgCarbonIntensities={data.carbonIntensities}
                pai11GhgEmissionsSum={data.pai11GhgEmissionsSum}
              />
            </WithGap>
          </Column>
        </Row>

        {/** @todo: Temporary taxonomy data need to be hidden, see SAMIK-1799 */}
        <Show when={false}>
          <EsgTaxonomyWidget
            euTaxonomyAlignment={data.euTaxonomyAlignment}
            euTaxonomyEligibility={data.euTaxonomyEligibility}
          />
        </Show>

        <WithGap>
          <WithGap direction="row" gap="16">
            <h2>{texts.pages.esg.solutions.title}</h2>

            <ConnectPopover
              id="esg-solutions"
              title={texts.pages.esg.solutions.helpTitle}
              description={texts.pages.esg.solutions.helpDescription}
            />
          </WithGap>

          <Row>
            <Column $span={6}>
              <WithGap gap="64">
                <div>
                  <p className="font-body">
                    {texts.pages.esg.solutions.description}
                  </p>
                  <p className="font-body">
                    {fmsWithTemplate(texts.pages.esg.solutions.overview, {
                      sustainablePercentage:
                        data.solutionCompanies.kpi.value.toString(),
                      greenPercentage: data.greenBond.kpi.value.toString(),
                    })}
                  </p>
                </div>

                <Center horizontal>
                  <Gauge
                    percent={data.solutionCompanies.kpi.value}
                    description={
                      texts.pages.esg.overview.sustainableInvestments
                    }
                    size={300}
                  />
                </Center>
              </WithGap>
            </Column>

            <Column $span={6}>
              <WithGap>
                <p className="font-body">
                  {fmsWithTemplate(texts.pages.esg.solutions.dividedInto, {
                    sustainablePercentage:
                      data.solutionCompanies.kpi.value.toString(),
                  })}
                </p>

                <EsgSolutionsChart
                  greenBond={data.greenBond}
                  solutionCompanies={data.solutionCompanies}
                  solutionCompaniesEqualOpportunities={
                    data.solutionCompaniesEqualOpportunities
                  }
                  solutionCompaniesRenewableEnergy={
                    data.solutionCompaniesRenewableEnergy
                  }
                  solutionCompaniesCircularEconomy={
                    data.solutionCompaniesCircularEconomy
                  }
                  solutionCompaniesSmartCities={
                    data.solutionCompaniesSmartCities
                  }
                  positionLegendBottom
                  hideDescription
                />
              </WithGap>
            </Column>
          </Row>

          <Row>
            <Column $span={12}>
              <WithGap>
                <div>
                  <div className="font-details-demibold">
                    {texts.pages.esg.solutions.companyRenewableEnergy}
                  </div>
                  <div className="font-details">
                    {
                      texts.pages.esg.solutions.infoText.companyRenewableEnergy
                        .description
                    }
                  </div>
                </div>

                <div>
                  <div className="font-details-demibold">
                    {texts.pages.esg.solutions.companySmartCities}
                  </div>
                  <div className="font-details">
                    {
                      texts.pages.esg.solutions.infoText.companySmartCities
                        .description
                    }
                  </div>
                </div>

                <div>
                  <div className="font-details-demibold">
                    {texts.pages.esg.solutions.companyCircularEconomy}
                  </div>
                  <div className="font-details">
                    {
                      texts.pages.esg.solutions.infoText.companyCircularEconomy
                        .description
                    }
                  </div>
                </div>

                <div>
                  <div className="font-details-demibold">
                    {texts.pages.esg.solutions.companyEqualOpportunities}
                  </div>
                  <div className="font-details">
                    {
                      texts.pages.esg.solutions.infoText
                        .companyEqualOpportunities.description
                    }
                  </div>
                </div>
              </WithGap>
            </Column>
          </Row>
        </WithGap>

        <WithGap>
          <h2>{texts.pages.esg.exclusions.title}</h2>

          <EsgExclusions />
        </WithGap>
      </WithGap>
    </WithGap>
  );
};
